/**
 * EchoDrawGame - Main orchestrator for the EchoDraw experience
 * Mobile-first, accessibility-focused, simple and joyful
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Mic, Play, Home, RotateCcw, Globe, MessageSquare, ArrowLeft, Layers, Plus, Eye, EyeOff, Palette, ChevronUp, Sparkles } from 'lucide-react';
import { audioEngine } from './AudioEngine';
import { SoundCanvas, pitchToColor } from './SoundCanvas';
import { playSound } from './PlaybackEngine';
import type { VisualMark, GamePhase, TextureType, Layer, PaletteTheme } from './types';
import { AnimatedBackground } from './AnimatedBackground';

interface EchoDrawGameProps {
  language: 'en' | 'es';
  onExit: () => void;
  onLanguageChange?: () => void;
  onFeedback?: () => void;
}

const TEXTURES: TextureType[] = ['circles', 'triangle', 'rectangle', 'hexagon', 'lines', 'dots', 'waves', 'stars', 'hearts', 'watercolor', 'splash', 'bloom', 'gradient'];

const LAYER_COLORS = ['#FF6B35', '#3B82F6', '#60A5FA', '#F38181', '#AA96DA', '#FCBAD3'];
const LAYER_TEMPOS = [200, 150, 250, 180, 220, 170]; // Different speeds for each layer (ms between marks)
const LAYER_START_DELAYS = [0, 20, 40, 60, 80, 0]; // Percentage of total time before layer starts (creates building effect)

export function EchoDrawGame({ language, onExit, onLanguageChange, onFeedback }: EchoDrawGameProps) {
  const [phase, setPhase] = useState<GamePhase>('intro');
  const [marks, setMarks] = useState<VisualMark[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const listeningIntervalRef = useRef<number>();
  const playbackCancelRef = useRef<{ [layerId: string]: boolean }>({});
  const lastTextureIndexRef = useRef<number>(0);

  // Layer management
  const [layers, setLayers] = useState<Layer[]>([
    { id: 'layer-1', name: 'Layer 1', marks: [], isActive: true, color: LAYER_COLORS[0], tempo: LAYER_TEMPOS[0], startDelay: LAYER_START_DELAYS[0] }
  ]);
  const [currentLayerIndex, setCurrentLayerIndex] = useState(0);

  // Palette theme
  const [paletteTheme, setPaletteTheme] = useState<PaletteTheme>('orange');
  const [showPaletteMenu, setShowPaletteMenu] = useState(false);

  const text = {
    en: {
      title: 'EchoDraw',
      subtitle: 'Where Sound Becomes Color, and Color Becomes Sound',
      intro: 'Make sounds with your voice - hums, whistles, claps - and watch them turn into colorful shapes!',
      startButton: 'Start Creating',
      permission: 'We need your microphone to hear your sounds',
      allowMic: 'Allow Microphone',
      listening: 'Make sounds! Your voice, your clapping and finger snapping are painting',
      pause: 'Pause',
      play: 'Hear Your Drawing Sing',
      tryAgain: 'Create New Drawing',
      goHome: 'Home',
      tip: 'Try different sounds: high, low, loud, soft!',
      beta: 'BETA',
      feedback: 'Feedback',
      wonderpath: 'Wonderpath',
      addLayer: 'Add Layer',
      layers: 'Layers',
      layer: 'Layer',
      record: 'Record',
      playLayer: 'Play Layer',
      accessibilityNote: 'Designed with accessibility in mind. We welcome your feedback on vision, sound, and interaction enhancements!'
    },
    es: {
      title: 'EchoDraw',
      subtitle: 'Donde el Sonido se Convierte en Color, y el Color se Convierte en Sonido',
      intro: '¡Haz sonidos con tu voz - tarareos, silbidos, aplausos - y míralos convertirse en formas coloridas!',
      startButton: 'Comenzar a Crear',
      permission: 'Necesitamos tu micrófono para escuchar tus sonidos',
      allowMic: 'Permitir Micrófono',
      listening: '¡Haz sonidos! Tu voz, tus aplausos y chasquidos de dedos están pintando',
      pause: 'Pausar',
      play: 'Escucha tu Dibujo Cantar',
      tryAgain: 'Crear Nuevo Dibujo',
      goHome: 'Inicio',
      tip: '¡Prueba diferentes sonidos: altos, bajos, fuertes, suaves!',
      beta: 'BETA',
      feedback: 'Comentarios',
      wonderpath: 'Wonderpath',
      addLayer: 'Agregar Capa',
      layers: 'Capas',
      layer: 'Capa',
      record: 'Grabar',
      playLayer: 'Reproducir Capa',
      accessibilityNote: '¡Diseñado con accesibilidad en mente. Valoramos sus comentarios sobre mejoras en visión, sonido e interacción!'
    }
  };

  const t = text[language];

  // Handle microphone permission
  const handleRequestMic = async () => {
    const granted = await audioEngine.requestMicrophoneAccess();
    if (granted) {
      setPhase('active');
      startListening();
    } else {
      alert(language === 'en' ? 'Microphone access is needed to play!' : '¡Se necesita acceso al micrófono para jugar!');
    }
  };

  // Start listening and creating visuals
  const startListening = () => {
    setIsListening(true);

    listeningIntervalRef.current = window.setInterval(() => {
      const sound = audioEngine.getSoundProperties();

      if (sound.isActive && sound.volume > 0.02) {
        // Better texture selection for variety: combine pitch with rotation
        // Use pitch to pick from groups, then rotate within the group
        const pitchGroup = Math.floor(sound.pitch * 4); // 4 groups based on pitch
        const groupSize = Math.ceil(TEXTURES.length / 4);
        const groupStart = pitchGroup * groupSize;

        // Rotate through textures more evenly
        lastTextureIndexRef.current = (lastTextureIndexRef.current + 1) % TEXTURES.length;

        // Mix of sequential and pitch-based for variety
        const shouldUseSequential = Math.random() > 0.3; // 70% sequential, 30% pitch-based
        const textureIndex = shouldUseSequential
          ? lastTextureIndexRef.current
          : Math.min(groupStart + Math.floor(Math.random() * groupSize), TEXTURES.length - 1);

        // Create a new visual mark with varied size and rotation
        const newMark: VisualMark = {
          id: `mark-${Date.now()}-${Math.random()}`,
          x: Math.random(),
          y: Math.random(),
          size: 20 + sound.volume * 100, // Increased size variation
          color: pitchToColor(sound.pitch, paletteTheme),
          texture: TEXTURES[textureIndex],
          opacity: 1, // Keep at 100%
          rotation: Math.random() * Math.PI * 2, // Random rotation 0-360 degrees
          createdAt: Date.now()
        };

        // Add mark to current layer
        setLayers(prevLayers => {
          const newLayers = [...prevLayers];
          newLayers[currentLayerIndex] = {
            ...newLayers[currentLayerIndex],
            marks: [...newLayers[currentLayerIndex].marks, newMark].slice(-50)
          };
          return newLayers;
        });

        // Also update marks for backward compatibility with canvas
        setMarks(prev => [...prev, newMark].slice(-50));
      }
    }, 100); // Check for sound 10 times per second
  };

  // Stop listening
  const stopListening = () => {
    setIsListening(false);
    if (listeningIntervalRef.current) {
      clearInterval(listeningIntervalRef.current);
    }
  };

  // Add a new layer
  const handleAddLayer = () => {
    const newLayerNumber = layers.length + 1;
    const newLayer: Layer = {
      id: `layer-${Date.now()}`,
      name: `${t.layer} ${newLayerNumber}`,
      marks: [],
      isActive: true,
      color: LAYER_COLORS[newLayerNumber % LAYER_COLORS.length],
      tempo: LAYER_TEMPOS[newLayerNumber % LAYER_TEMPOS.length],
      startDelay: LAYER_START_DELAYS[newLayerNumber % LAYER_START_DELAYS.length]
    };
    setLayers([...layers, newLayer]);
    setCurrentLayerIndex(layers.length);
    stopListening(); // Don't auto-start recording
  };

  // Select a layer to record on
  const handleSelectLayer = (layerIndex: number) => {
    stopListening();
    setCurrentLayerIndex(layerIndex);
    setMarks(layers[layerIndex].marks);
    setPhase('active');
  };

  // Start recording on current layer
  const handleStartRecording = () => {
    if (phase === 'active' && !isListening) {
      startListening();
    }
  };

  // Play a specific layer
  const handlePlayLayer = async (layerIndex: number) => {
    const layer = layers[layerIndex];
    if (layer.marks.length === 0) return;

    setIsPlaying(true);

    // Play each mark in the layer with the layer's tempo
    for (const mark of layer.marks) {
      playSound(mark.color, mark.size); // Don't await - let sounds overlap
      await new Promise(resolve => setTimeout(resolve, layer.tempo));
    }

    setIsPlaying(false);
  };

  // Toggle layer visibility (works during playback for real-time mixing)
  const handleToggleLayer = (layerIndex: number) => {
    setLayers(prevLayers => {
      const newLayers = [...prevLayers];
      const layerId = newLayers[layerIndex].id;
      const newState = !newLayers[layerIndex].isActive;

      newLayers[layerIndex] = {
        ...newLayers[layerIndex],
        isActive: newState
      };

      // If turning off during playback, signal the layer to stop playing new sounds
      if (!newState && isPlaying) {
        playbackCancelRef.current[layerId] = true;
      } else if (newState && isPlaying) {
        // If turning back on during playback, allow it to play again
        playbackCancelRef.current[layerId] = false;
      }

      return newLayers;
    });
  };

  // Get all marks from all active layers
  const getAllActiveMarks = (): VisualMark[] => {
    return layers
      .filter(layer => layer.isActive)
      .flatMap(layer => layer.marks);
  };

  // Play back the drawing as sound (all active layers with sequencing and independent tempos)
  const handlePlayback = async () => {
    setIsPlaying(true);
    setPhase('playback');

    // Reset cancellation flags
    playbackCancelRef.current = {};

    // Get all active layers
    const activeLayers = layers.filter(layer => layer.isActive && layer.marks.length > 0);

    if (activeLayers.length === 0) {
      setIsPlaying(false);
      setPhase('complete');
      return;
    }

    // Calculate total duration of the longest layer
    const layerDurations = activeLayers.map(layer => layer.marks.length * layer.tempo);
    const maxDuration = Math.max(...layerDurations);

    // Play each layer independently with its own tempo and start delay
    const layerPlaybacks = activeLayers.map(async (layer) => {
      // Calculate when this layer should start (based on startDelay percentage)
      const delayTime = (maxDuration * layer.startDelay) / 100;

      // Wait for the calculated delay before starting this layer
      await new Promise(resolve => setTimeout(resolve, delayTime));

      // Initialize this layer as not cancelled
      playbackCancelRef.current[layer.id] = false;

      // Play each mark in this layer at its own tempo
      for (const mark of layer.marks) {
        // Check if this layer has been toggled off during playback
        if (playbackCancelRef.current[layer.id]) {
          break; // Stop playing this layer
        }
        playSound(mark.color, mark.size); // Fire and forget - sounds overlap naturally
        await new Promise(resolve => setTimeout(resolve, layer.tempo));
      }
    });

    // Wait for all layers to finish playing
    await Promise.all(layerPlaybacks);

    setIsPlaying(false);
    setPhase('complete');
  };

  // Reset and create new (clears all layers and starts fresh)
  const handleReset = () => {
    stopListening();
    setMarks([]);
    setLayers([{ id: 'layer-1', name: `${t.layer} 1`, marks: [], isActive: true, color: LAYER_COLORS[0], tempo: LAYER_TEMPOS[0], startDelay: LAYER_START_DELAYS[0] }]);
    setCurrentLayerIndex(0);
    setPhase('active');
    startListening();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      audioEngine.stop();
    };
  }, []);

  // Cleanup audio when closing browser/tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopListening();
      audioEngine.stop();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle exit with audio cleanup
  const handleExit = () => {
    stopListening();
    audioEngine.stop();
    onExit();
  };

  // Header Component
  function Header({ variant = 'absolute' }: { variant?: 'absolute' | 'flex' }) {
    return (
    <div className={`z-50 bg-gradient-to-r from-amber-50/95 to-yellow-50/95 backdrop-blur-sm border-b border-amber-200/50 ${
      variant === 'absolute' ? 'absolute top-0 left-0 right-0' : 'flex-shrink-0'
    }`}>
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Beta badge and Wonderpath title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-base font-semibold text-amber-900">
                {t.beta}
              </span>
            </div>
            <span className="text-lg font-bold text-amber-900 hidden sm:inline">
              {t.wonderpath}
            </span>
          </div>

          {/* Right: Language toggle and Feedback button */}
          <div className="flex items-center gap-2">
            {onLanguageChange && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onLanguageChange}
                className="gap-2 text-amber-900 hover:bg-amber-100"
                aria-label={language === 'en' ? 'Switch to Spanish' : 'Cambiar a inglés'}
              >
                <Globe className="w-4 h-4" />
                <span className="font-semibold">{language === 'en' ? 'ES' : 'EN'}</span>
              </Button>
            )}
            {onFeedback && (
              <Button
                onClick={onFeedback}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2 shadow-sm"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">{t.feedback}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  }

  // INTRO SCREEN
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4 relative">
        <AnimatedBackground />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center relative z-10"
        >
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-4 drop-shadow-lg">
            {t.title}
          </h1>
          <p className="text-4xl text-white font-semibold mb-6 drop-shadow-lg">
            {t.subtitle}
          </p>
          <p className="text-2xl text-white font-medium mb-8 px-4 drop-shadow-md">
            {t.intro}
          </p>

          <Button
            onClick={() => setPhase('permission')}
            size="lg"
            className="w-full max-w-sm h-16 text-4xl font-bold text-white bg-gradient-to-r from-[#DE8F05] to-[#FE6100] hover:from-[#C57D04] hover:to-[#E55700] shadow-lg"
          >
            {t.startButton}
          </Button>
        </motion.div>
      </div>
    );
  }

  // PERMISSION SCREEN
  if (phase === 'permission') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4 relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="text-8xl mb-6">🎤</div>
          <h2 className="text-5xl font-bold text-white mb-4">
            {t.permission}
          </h2>

          <Button
            onClick={handleRequestMic}
            size="lg"
            className="w-full h-16 text-3xl font-bold bg-orange-500 hover:bg-orange-600 mb-4"
          >
            <Mic className="w-6 h-6 mr-2" />
            {t.allowMic}
          </Button>

          <Button
            onClick={() => setPhase('intro')}
            variant="outline"
            className="border-2 border-white bg-white text-black hover:bg-white/90"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {language === 'en' ? 'Back' : 'Volver'}
          </Button>
        </motion.div>
      </div>
    );
  }

  // ACTIVE / PLAYBACK / COMPLETE SCREEN
  return (
    <div className="h-screen w-screen bg-black flex flex-col overflow-hidden relative">
      <Header variant="flex" />
      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden">
        <SoundCanvas
          marks={marks}
          layers={layers}
          isListening={isListening}
          isPlaying={isPlaying}
          paletteTheme={paletteTheme}
        />


        {/* Layer Panel */}
        {(phase === 'active' || phase === 'playback' || phase === 'complete') && (
          <motion.div
            initial={{ x: 300 }}
            animate={{ x: 0 }}
            className="absolute right-4 top-4 bg-black/80 backdrop-blur rounded-lg p-4 max-w-xs w-64 max-h-96 overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-white" />
                <h3 className="text-white font-bold">{t.layers}</h3>
              </div>
              <span className="text-white/60 text-lg">{layers.length}</span>
            </div>

            {/* Layer List */}
            <div className="space-y-2 mb-3">
              {layers.map((layer, index) => (
                <div
                  key={layer.id}
                  className={`rounded p-2 transition-all ${
                    index === currentLayerIndex
                      ? 'bg-blue-500/30 border-2 border-blue-400'
                      : 'bg-white/10 border-2 border-transparent hover:bg-white/20'
                  } ${!layer.isActive ? 'opacity-40' : 'opacity-100'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {/* Layer color indicator */}
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: layer.color }}
                    />

                    {/* Layer name - clickable to select */}
                    <div className="flex-1">
                      <button
                        onClick={() => handleSelectLayer(index)}
                        className="text-white text-lg hover:text-blue-300 transition-colors font-semibold block"
                      >
                        {layer.name}
                      </button>
                      {layer.startDelay > 0 && (
                        <span className="text-base text-cyan-400 font-medium">
                          ⏱ Starts at {layer.startDelay}%
                        </span>
                      )}
                    </div>

                    {/* Recording indicator */}
                    {index === currentLayerIndex && isListening && (
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-base text-red-400 font-bold">REC</span>
                      </div>
                    )}

                    {/* Toggle visibility button */}
                    <button
                      onClick={() => handleToggleLayer(index)}
                      className={`transition-all p-1 rounded ${
                        layer.isActive
                          ? 'text-green-400 hover:text-green-300 bg-green-500/20'
                          : 'text-red-400 hover:text-red-300 bg-red-500/20'
                      }`}
                      title={layer.isActive ? 'Hide layer (click to mute)' : 'Show layer (click to unmute)'}
                    >
                      {layer.isActive ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Layer controls */}
                  <div className="flex gap-1.5">
                    {/* Record button (only for current layer) */}
                    {index === currentLayerIndex && (
                      <Button
                        onClick={isListening ? stopListening : handleStartRecording}
                        size="sm"
                        className={`flex-1 text-base py-2 px-2 font-semibold transition-colors ${
                          isListening
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <Mic className="w-3.5 h-3.5" />
                          {isListening ? t.pause : t.record}
                        </div>
                      </Button>
                    )}

                    {/* Play button */}
                    <Button
                      onClick={() => handlePlayLayer(index)}
                      disabled={layer.marks.length === 0 || isPlaying}
                      size="sm"
                      className="flex-1 text-base py-2 px-2 font-semibold bg-blue-500 hover:bg-blue-600 disabled:bg-gray-500 disabled:opacity-50 text-white transition-colors"
                      title={t.playLayer}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <Play className="w-3.5 h-3.5" />
                        {language === 'en' ? 'Play' : 'Reproducir'}
                      </div>
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Layer Button */}
            <Button
              onClick={handleAddLayer}
              size="sm"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              {t.addLayer}
            </Button>
          </motion.div>
        )}
      </div>

      {/* Controls */}
      <div className="flex-shrink-0 p-4 bg-black/90 backdrop-blur border-t border-white/10">
        {/* Status Text - Make Sounds Prompt */}
        <AnimatePresence>
          {isListening && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="max-w-lg mx-auto mb-3 text-center"
            >
              <div className="bg-black/80 backdrop-blur-md rounded-full px-6 py-3 inline-block border-2 border-red-500">
                <div className="flex items-center justify-center gap-3 mb-1">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <p className="text-white text-2xl md:text-3xl font-bold">
                    {t.listening}
                  </p>
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                </div>
                <p className="text-yellow-300 text-xl md:text-2xl font-bold">
                  {t.tip}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-lg mx-auto flex flex-wrap gap-2 sm:gap-3 items-center justify-center">
          {phase === 'active' && isListening && (
            <>
              <Button
                onClick={stopListening}
                size="lg"
                className="flex-1 min-w-[140px] h-16 sm:h-14 text-xl sm:text-2xl font-bold text-white bg-orange-400 hover:bg-orange-500 whitespace-normal leading-tight px-3"
              >
                {t.pause}
              </Button>

              {/* Palette Selector */}
              <div className="relative">
                <Button
                  onClick={() => setShowPaletteMenu(!showPaletteMenu)}
                  size="lg"
                  className="h-16 sm:h-14 px-3 sm:px-4 bg-purple-500 hover:bg-purple-600 text-white relative"
                  title="Color Palette"
                >
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Palette className="w-5 h-5 flex-shrink-0" />
                    <ChevronUp className={`w-4 h-4 flex-shrink-0 transition-transform ${showPaletteMenu ? 'rotate-180' : ''}`} />
                  </div>
                  {!showPaletteMenu && (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-1 -right-1"
                    >
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                    </motion.div>
                  )}
                </Button>

                {showPaletteMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-16 right-0 bg-black/90 backdrop-blur rounded-lg p-2 space-y-2 min-w-[200px] border border-white/20"
                  >
                    <button
                      onClick={() => { setPaletteTheme('orange'); setShowPaletteMenu(false); }}
                      className={`w-full p-3 rounded text-left transition-all ${
                        paletteTheme === 'orange' ? 'bg-orange-500/50 border-2 border-orange-400' : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-4 h-4 rounded-full bg-orange-400" />
                          <div className="w-4 h-4 rounded-full bg-yellow-300" />
                          <div className="w-4 h-4 rounded-full bg-amber-500" />
                        </div>
                        <span className="text-white text-lg font-semibold">Orange Theme</span>
                      </div>
                    </button>

                    <button
                      onClick={() => { setPaletteTheme('blackWhite'); setShowPaletteMenu(false); }}
                      className={`w-full p-3 rounded text-left transition-all ${
                        paletteTheme === 'blackWhite' ? 'bg-gray-500/50 border-2 border-gray-400' : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-4 h-4 rounded-full bg-white" />
                          <div className="w-4 h-4 rounded-full bg-gray-300" />
                          <div className="w-4 h-4 rounded-full bg-gray-500" />
                        </div>
                        <span className="text-white text-lg font-semibold">Black & White</span>
                      </div>
                    </button>

                    <button
                      onClick={() => { setPaletteTheme('patterns'); setShowPaletteMenu(false); }}
                      className={`w-full p-3 rounded text-left transition-all ${
                        paletteTheme === 'patterns' ? 'bg-blue-500/50 border-2 border-blue-400' : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-4 h-4 rounded-full bg-blue-500 relative overflow-hidden">
                            <div className="absolute inset-0 bg-yellow-400" style={{
                              background: 'repeating-linear-gradient(45deg, #3B82F6, #3B82F6 2px, #FBBF24 2px, #FBBF24 4px)'
                            }} />
                          </div>
                          <div className="w-4 h-4 rounded-full bg-blue-500" />
                          <div className="w-4 h-4 rounded-full bg-yellow-400" />
                        </div>
                        <span className="text-white text-lg font-semibold">Patterns</span>
                      </div>
                    </button>
                  </motion.div>
                )}
              </div>
            </>
          )}

          {phase === 'active' && !isListening && marks.length > 0 && (
            <Button
              onClick={handlePlayback}
              size="lg"
              className="flex-1 min-w-[180px] h-16 sm:h-14 text-xl sm:text-2xl font-bold text-white bg-blue-500 hover:bg-blue-600 whitespace-normal leading-tight px-3"
            >
              <Play className="w-5 h-5 mr-2 flex-shrink-0" fill="white" />
              <span>{t.play}</span>
            </Button>
          )}

          {(phase === 'playback' || phase === 'complete') && (
            <>
              <Button
                onClick={handlePlayback}
                size="lg"
                disabled={isPlaying}
                className="flex-1 min-w-[160px] h-16 sm:h-14 text-xl sm:text-2xl font-bold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-normal leading-tight px-3"
              >
                <Play className="w-5 h-5 mr-2 flex-shrink-0" fill="white" />
                <span>{t.play}</span>
              </Button>

              <Button
                onClick={handleReset}
                size="lg"
                className="flex-1 min-w-[160px] h-16 sm:h-14 text-xl sm:text-2xl bg-yellow-400 hover:bg-yellow-500 text-black font-bold whitespace-normal leading-tight px-3"
              >
                <RotateCcw className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{t.tryAgain}</span>
              </Button>
            </>
          )}
        </div>

        {/* Accessibility Note */}
        <div className="max-w-lg mx-auto mt-4 text-center">
          <p className="text-lg sm:text-xl text-white font-semibold leading-relaxed">
            {t.accessibilityNote}
          </p>
        </div>
      </div>
    </div>
  );
}
